/**
 * Standalone-web login page — shown when the user opens /app/ outside
 * Telegram (no initData). Queries /api/auth/providers and renders only
 * the providers the admin has enabled.
 *
 * Provider flow:
 *   telegram — Telegram Login Widget rendered inside the browser
 *   email    — magic link: POST /api/auth/email/begin → inbox wait screen
 *   google   — redirect to /api/auth/google/begin
 */

import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Chrome, ExternalLink, Loader2, Mail, MessageCircle, Send, User } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { hasRealLogin, useAuthStore } from '@/lib/auth'
import { getT } from '@/lib/i18n'

type Lang = 'uk' | 'en' | 'ru'

declare global {
  interface Window {
    onTelegramAuth?: (user: Record<string, unknown>) => void
  }
}

function LoginMethodCard({
  icon,
  title,
  hint,
  children,
}: {
  icon: ReactNode
  title: string
  hint: string
  children: ReactNode
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
          {icon}
        </span>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{hint}</p>
        </div>
      </div>
      {children}
    </section>
  )
}

function TelegramSection({
  lang, botName, onSuccess,
}: {
  lang: Lang
  botName: string | undefined
  onSuccess: () => void
}) {
  const T = getT(lang, 'login')
  const containerRef = useRef<HTMLDivElement>(null)
  const injected = useRef(false)

  useEffect(() => {
    if (!containerRef.current || !botName || injected.current) return
    injected.current = true

    window.onTelegramAuth = async (user) => {
      try {
        await api.post('/auth/telegram', user)
        onSuccess()
      } catch {
        /* widget renders its own error UI */
      }
    }

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.setAttribute('data-telegram-login', botName)
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-radius', '12')
    script.setAttribute('data-onauth', 'onTelegramAuth(user)')
    script.setAttribute('data-request-access', 'write')
    script.async = true
    containerRef.current.appendChild(script)

    return () => { window.onTelegramAuth = undefined }
  }, [botName, onSuccess])

  return (
    <div className="flex min-h-[52px] items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-3 py-4">
      {!botName
        ? <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
        : <div ref={containerRef} />
      }
      {!botName && <span className="sr-only">{T.loading}</span>}
    </div>
  )
}

function EmailForm({ lang }: { lang: Lang }) {
  const T = getT(lang, 'login')
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  async function submit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!email.trim()) return
    setSending(true)
    setError('')
    try {
      await api.post('/auth/email/begin', { email })
      setSent(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : T.emailError)
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className="space-y-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-center">
        <p className="text-sm font-medium text-emerald-700">✉️ {T.emailSent}</p>
        <button
          onClick={() => { setSent(false); setEmail('') }}
          className="text-xs text-emerald-700 underline underline-offset-2"
        >
          {T.emailResend}
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={T.emailPlaceholder}
          className="min-w-0 flex-1 rounded-2xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
        <button
          type="submit"
          disabled={sending || !email.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white disabled:opacity-40"
        >
          {sending
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Send className="h-4 w-4" />}
          {sending ? T.emailSending : T.emailBtn}
        </button>
      </div>
      {error && <p className="text-xs text-rose-500">{error}</p>}
    </form>
  )
}

interface PublicSettings {
  bot_username: string
  shop_name: string
  brand_logo_url: string | null
  supported_languages?: string[]
}

export function LoginPage() {
  const navigate = useNavigate()
  const authStatus = useAuthStore((s) => s.status)
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated)
  const [guestError, setGuestError] = useState('')
  const [guestLoading, setGuestLoading] = useState(false)

  const { data: providersData, isLoading } = useQuery<{ providers: string[] }>({
    queryKey: ['auth-providers'],
    queryFn: () => api.get('/auth/providers'),
    staleTime: 60_000,
  })

  const { data: settings, isLoading: settingsLoading } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const supportedLanguages = settings?.supported_languages ?? ['uk', 'en', 'ru']
  const browserLang = navigator.language.startsWith('en') ? 'en' : 'uk'
  const firstSupportedLang = supportedLanguages.find(
    (language): language is Lang => language === 'uk' || language === 'en' || language === 'ru',
  )
  const lang: Lang = supportedLanguages.includes(browserLang)
    ? browserLang
    : supportedLanguages.includes('ru')
      ? 'ru'
      : (firstSupportedLang ?? 'uk')
  const T = getT(lang, 'login')

  useEffect(() => {
    if (authStatus !== 'authenticated') return
    void hasRealLogin().then((loggedIn) => {
      if (loggedIn) void navigate({ to: '/catalog' })
    })
  }, [authStatus, navigate])

  function onAuthSuccess() {
    void navigate({ to: '/catalog' })
  }

  async function continueAsGuest() {
    setGuestLoading(true)
    setGuestError('')
    try {
      const guestData = await api.post<{ user_id: number; role: string }>('/auth/guest', {})
      setAuthenticated(guestData.user_id, guestData.role)
      void navigate({ to: '/catalog' })
    } catch {
      setGuestError(T.guestError)
    } finally {
      setGuestLoading(false)
    }
  }

  if (isLoading || settingsLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[radial-gradient(circle_at_top,#e0f2fe_0%,#f8fafc_38%,#f8fafc_100%)]">
        <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
      </div>
    )
  }

  const providers = providersData?.providers ?? []
  const botName = settings?.bot_username
  const shopName = settings?.shop_name

  const hasTelegram = providers.includes('telegram') && Boolean(botName)
  const hasGoogle = providers.includes('google')
  const hasEmail = providers.includes('email')
  const hasAny = hasTelegram || hasGoogle || hasEmail

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#dbeafe_0%,#f8fafc_34%,#f8fafc_100%)] px-5 py-10">
      <div className="mx-auto w-full max-w-xl">
        <div className="mb-8 text-center">
          {settings?.brand_logo_url ? (
            <div className="mx-auto mb-4 flex h-18 w-fit items-center justify-center rounded-[28px] bg-brand-600 px-4 shadow-[0_18px_40px_rgba(2,132,199,0.22)]">
              <img
                src={settings.brand_logo_url}
                alt={shopName ?? ''}
                className="h-12 max-w-[200px] object-contain"
              />
            </div>
          ) : (
            <div className="mx-auto mb-4 flex h-18 w-18 items-center justify-center rounded-[28px] bg-sky-600 text-3xl shadow-[0_18px_40px_rgba(2,132,199,0.22)]">
              🛍
            </div>
          )}
          {shopName && (
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-sky-700">
              {shopName}
            </p>
          )}
          <h1 className="text-3xl font-bold tracking-tight text-slate-950">{T.title}</h1>
          {hasAny && (
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">{T.subtitle}</p>
          )}
        </div>

        {!hasAny && (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 shadow-sm">
            {T.noProviders}
          </div>
        )}

        {hasAny && (
          <div className="space-y-4">
            {hasTelegram && (
              <LoginMethodCard
                icon={<MessageCircle className="h-5 w-5" />}
                title={T.telegramTitle}
                hint={T.telegramHint}
              >
                <TelegramSection lang={lang} botName={botName} onSuccess={onAuthSuccess} />
              </LoginMethodCard>
            )}

            {hasGoogle && (
              <LoginMethodCard
                icon={<Chrome className="h-5 w-5" />}
                title={T.googleTitle}
                hint={T.googleHint}
              >
                <a
                  href="/api/auth/google/begin"
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                >
                  <Chrome className="h-4 w-4 text-sky-600" />
                  {T.google}
                </a>
              </LoginMethodCard>
            )}

            {hasEmail && (
              <LoginMethodCard
                icon={<Mail className="h-5 w-5" />}
                title={T.emailTitle}
                hint={T.emailHint}
              >
                <EmailForm lang={lang} />
              </LoginMethodCard>
            )}
          </div>
        )}

        <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-5 text-center shadow-sm">
          <button
            type="button"
            onClick={continueAsGuest}
            disabled={guestLoading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50"
          >
            {guestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <User className="h-4 w-4" />}
            {T.guest}
          </button>
          <p className="mt-2 text-xs leading-5 text-slate-500">{T.guestHint}</p>
          {guestError && <p className="mt-2 text-xs text-rose-500">{guestError}</p>}
        </div>

        {botName && (
          <div className="mt-5 rounded-3xl border border-sky-100 bg-sky-50/80 p-4 text-center shadow-sm">
            <p className="text-sm font-medium text-sky-900">{T.openTelegram}</p>
            <p className="mt-1 text-xs leading-5 text-sky-700">{T.openTelegramHint}</p>
            <a
              href={`https://t.me/${botName}`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-2xl border border-sky-200 bg-white px-4 py-2.5 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-100"
            >
              <ExternalLink className="h-4 w-4" />
              {T.openTelegram}
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
